import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationJson from './translation.json';
import * as Constants from '../utils/constants';

/**
 * The translation.json file contains the translation for all languages.
 * The structure of the file is something like this
 *
 *      {
 *          translations: {
 *              en: {
 *                  default: {
 *                      "app.title": "MODE CS Tool",
 *                      "label.email": "email",
 *                      ...
 *                  }
 *              },
 *              ja: {
 *                  default: {
 *                      "app.title": "MODEカスタマーサポートツール",
 *                      "label.email": "メール",
 *                      ...
 *                  }
 *              }
 *          },
 *      }
 *
 * if a project needs customised naming then translations can be provided via
 * MCRMT project set up to override the default translations
 */
i18n.use(initReactI18next).init({
    resources    : translationJson.translations,
    lng          : Constants.DEFAULT_LANGUAGE.code,
    fallbackLng  : 'en',
    ns           : ['default', 'override'],   // The list of namespaces including 'default'.
    defaultNS    : 'default',
    fallbackNS   : 'default',
    keySeparator : false, // we do not use keys in form messages.welcome
    nsSeparator  : false, // we do not need namespace separator
    interpolation: {
        escapeValue    : false, // react already safes from xss
        skipOnVariables: false,
        format         : (value: any, format?: string, language?: string, ...interpolations): string => {
            if (format) {
                if (format === 'number-format') {
                    // eslint-disable-next-line dot-notation
                    const decimalPlaces = interpolations[0]?.['decimalPlaces'] !== undefined ? Number(interpolations[0]['decimalPlaces']) : undefined;
                    return new Intl.NumberFormat(language, { maximumFractionDigits: decimalPlaces }).format(value);
                }
                if (format.startsWith('date-format-')) {
                    // Because i18next doesn't have a way to pass in options, we have to pass them in through the format name
                    // e.g.
                    //      'date-format-full' - Display full date and time
                    //      'date-format-date' - Display date only
                    //      'date-format-time' - Display time only
                    const formatOption = format.replace('date-format-', '');    // Remove the first part of the format
                    const options: any = {};

                    if (formatOption === 'date' || formatOption === 'compact' || formatOption === 'full') {
                        options.month = formatOption === 'compact' ? '2-digit' : 'short';
                        options.day = '2-digit';
                        options.year = formatOption === 'compact' ? '2-digit' : 'numeric';
                        options.hour12 = false;
                    }

                    if (formatOption === 'time' || formatOption === 'compact' || formatOption === 'full') {
                        options.hour = '2-digit';
                        options.minute = '2-digit';
                        options.second = '2-digit';
                        options.hour12 = false;
                    }

                    // If caller pass in a date string instead of date Object, convert it to date value as number
                    const dateValue: Date = (value instanceof Date || !Number.isNaN(Number(value))) ? value : Date.parse(value);

                    return new Intl.DateTimeFormat(language, options).format(dateValue);
                }
                if (format.startsWith('currency-format-')) {
                    // Because i18next doesn't have a way to pass in country code, we have to pass them in through the format name
                    // e.g. 'currency-format-US', 'currency-format-JP', 'currency-format-CN'. The last 2 characters in format name
                    // will be the country code. And then from that country code, we can lookup the currency that country use.
                    const countryCode = format.replace('currency-format-', '').toUpperCase();

                    // Lookup currency by country code. Default is USD.
                    return new Intl.NumberFormat(language, {
                        style   : 'currency',
                        currency: Constants.SUPPORTED_COUNTRIES_JSON[countryCode].currency.code || 'USD',
                    }).format(value);
                }
            }
            return value;
        },
    },
});

export { i18n };
